import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import Modal from '../Modal';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import { formatDateTime } from '../../utils/format';

/**
 * FIX-D300-S2 (D-300 sec.2-3) — "שליחת הודעה למנהל": the employee sees the fixed text before sending ("הלקוח <שם>
 * שלח קריאה לגבי <נושא>, ואין לו תקופת אחריות, בנק שעות או חוזה שירות"), the request is linked, and a free
 * text may be added. Sent to the customer's account manager and the OTE managers. `blocked` = the switch is on
 * (the dialog opens from a refused action and says so); otherwise it is a request for permission.
 */
export default function CoverageRequestDialog({
  open, onClose, ticketId, organizationName, ticketTitle, blocked, pendingSince,
}: {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  organizationName: string | null | undefined;
  ticketTitle: string | null | undefined;
  blocked: boolean;
  /** A request already waits (sent at): nothing to send again. */
  pendingSince?: string | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [extra, setExtra] = useState('');
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState<string | null>(pendingSince ?? null);

  useEffect(() => {
    if (open) { setExtra(''); setSentAt(pendingSince ?? null); }
  }, [open, pendingSince]);

  const send = async () => {
    setSending(true);
    try {
      const res = await api.createCoverageRequest(ticketId, extra.trim() || null);
      const at = res?.data?.request?.created_at ?? new Date().toISOString();
      setSentAt(at);
      toast('success', res?.data?.already ? t('coverage.alreadySent') : t('coverage.sent'));
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      onClose();
    } catch (err) {
      toast('error', apiErrorMessage(err, t('coverage.sendFailed')));
    } finally {
      setSending(false);
    }
  };

  const text = t('coverage.managerMessage', { customer: organizationName ?? '', subject: ticketTitle ?? '' });
  return (
    <Modal open={open} onClose={onClose} title={t('coverage.dialogTitle')} icon={<ShieldAlert size={18} aria-hidden="true" />}>
      <div className="space-y-4" data-testid="coverage-request-dialog">
        {blocked && <p className="text-sm font-medium text-danger-700">{t('coverage.blockedMessage')}</p>}
        <div className="rounded-lg border border-border bg-surface-50 p-3 text-sm">
          <p className="text-text-2">{t('coverage.messagePreview')}</p>
          <p className="mt-1 text-text" dir="auto" data-testid="coverage-request-text">{text}</p>
          <Link to={`/tickets/${ticketId}`} className="mt-2 inline-block text-sm text-primary-600 underline" onClick={onClose}>
            {t('coverage.openTicket')}
          </Link>
        </div>
        {sentAt ? (
          <p className="text-sm text-text-2" role="status">{t('coverage.pendingSince', { at: formatDateTime(sentAt) })}</p>
        ) : (
          <div>
            <label htmlFor="coverage-extra" className="mb-1 block text-sm font-medium text-text">{t('coverage.extraLabel')}</label>
            <textarea id="coverage-extra" rows={3} className="input" value={extra} maxLength={4000} dir="auto"
              onChange={(e) => setExtra(e.target.value)} />
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.close')}</button>
          {!sentAt && (
            <button type="button" className="btn btn-primary" disabled={sending} onClick={() => void send()} data-testid="coverage-send">
              {t('coverage.sendToManager')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
